import { useEffect, useMemo, useState } from "react";
import { Loader2, ShieldCheck, LifeBuoy, Search, X } from "lucide-react";
import { apiClient } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { LegalBody } from "@/components/LegalBody";
import { Input } from "@/components/ui/input";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";

const SUPPORT_EMAIL = "support@monoepay.com";

const FILTERS = [
  { id: "all", label: "All" },
  { id: "Everyone", label: "Everyone" },
  { id: "Customers", label: "Customers" },
  { id: "Businesses", label: "Businesses" },
  { id: "Workers", label: "Workers" },
];

const LegalHero = () => (
  <section className="relative overflow-hidden border-b border-border">
    <div className="pointer-events-none absolute inset-0 grid-bg opacity-50 [mask-image:radial-gradient(ellipse_at_top,black,transparent_75%)]" />
    <div className="relative mx-auto max-w-3xl px-4 py-14 sm:px-6 lg:px-8 lg:py-16">
      <Reveal><Eyebrow>Legal Center</Eyebrow></Reveal>
      <Reveal delay={0.05}>
        <h1 className="mt-4 font-display text-3xl font-extrabold leading-[1.05] tracking-tight sm:text-4xl">
          Policies & agreements
        </h1>
      </Reveal>
      <Reveal delay={0.1}>
        <div
          data-testid="legal-support-note"
          className="mt-5 flex items-start gap-2.5 rounded-lg border border-border bg-card px-4 py-3 text-sm text-muted-foreground"
        >
          <LifeBuoy className="mt-0.5 h-4 w-4 shrink-0 text-foreground" />
          <p>
            For general, account, payment, or merchant questions, please contact{" "}
            <a href={`mailto:${SUPPORT_EMAIL}`} data-testid="legal-support-link" className="font-medium text-foreground underline underline-offset-2">
              MONOE Customer Support
            </a>{" "}
            first. Tap any policy below to read it in full.
          </p>
        </div>
      </Reveal>
    </div>
  </section>
);

const LegalFilters = ({ filter, onChange }) => (
  <div className="mb-6 flex flex-wrap gap-2">
    {FILTERS.map((f) => (
      <button
        key={f.id}
        type="button"
        data-testid={`legal-filter-${f.id.toLowerCase()}`}
        onClick={() => onChange(f.id)}
        className={`rounded-md border px-3 py-1.5 text-xs font-medium transition-colors ${
          filter === f.id ? "border-foreground bg-foreground text-background" : "border-border text-muted-foreground hover:bg-accent"
        }`}
      >
        {f.label}
      </button>
    ))}
  </div>
);

const DocMeta = ({ doc }) => (
  <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 border-b border-border pb-4 font-mono-ui text-[11px] text-muted-foreground">
    <span>Version {doc.version}</span>
    {doc.effective_date && <span>Effective {doc.effective_date}</span>}
    {doc.last_updated && <span>Updated {doc.last_updated}</span>}
    {doc.audience && <span>Audience: {doc.audience}</span>}
    {doc.acceptance_required && (
      <span className="inline-flex items-center gap-1 rounded-full bg-foreground px-2 py-0.5 text-background">
        <ShieldCheck className="h-3 w-3" /> Acceptance required
      </span>
    )}
  </div>
);

const DocContent = ({ slug, cached, onLoaded }) => {
  const [state, setState] = useState(cached ? "ready" : "loading");

  useEffect(() => {
    if (cached) {
      setState("ready");
      return;
    }
    let active = true;
    setState("loading");
    apiClient
      .get(`/legal/${slug}`)
      .then(({ data }) => {
        if (!active) return;
        onLoaded(slug, data);
        setState("ready");
      })
      .catch(() => active && setState("error"));
    return () => {
      active = false;
    };
  }, [slug, cached, onLoaded]);

  if (state === "loading") {
    return (
      <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
      </div>
    );
  }
  if (state === "error" || !cached) {
    return <p className="py-4 text-sm text-muted-foreground">Couldn’t load this document. Please try again.</p>;
  }
  return (
    <div className="pb-2">
      <DocMeta doc={cached} />
      <LegalBody body={cached.body} className="mt-5 space-y-5" />
    </div>
  );
};

const Legal = () => {
  useSeo({
    title: "Legal Center",
    description: "MONOE Pay LLC legal documents — Terms, Privacy, Payments, Merchant, Worker & Tip Terms, Refunds, Acceptable Use, Cookies, POS integrations and more.",
    path: "/legal",
  });

  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState("");
  const [bodies, setBodies] = useState({});

  useEffect(() => {
    apiClient
      .get("/legal")
      .then(({ data }) => setDocs(data.documents || []))
      .catch(() => setDocs([]))
      .finally(() => setLoading(false));
  }, []);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return docs.filter((doc) => {
      const audienceOk = filter === "all" || (doc.audience || "").includes(filter);
      const queryOk =
        !q || (doc.title || "").toLowerCase().includes(q) || (doc.summary || "").toLowerCase().includes(q);
      return audienceOk && queryOk;
    });
  }, [docs, filter, query]);

  const cacheBody = (slug, data) => setBodies((prev) => ({ ...prev, [slug]: data }));

  const handleFilter = (id) => {
    setFilter(id);
    setOpen("");
  };

  const handleQuery = (value) => {
    setQuery(value);
    setOpen("");
  };

  return (
    <div>
      <LegalHero />
      <section>
        <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
          <div className="relative mb-4">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="text"
              value={query}
              onChange={(e) => handleQuery(e.target.value)}
              data-testid="legal-search-input"
              placeholder="Search policies…"
              className="pl-9 pr-9"
            />
            {query && (
              <button
                type="button"
                data-testid="legal-search-clear"
                aria-label="Clear search"
                onClick={() => handleQuery("")}
                className="absolute right-2.5 top-1/2 grid h-6 w-6 -translate-y-1/2 place-items-center rounded text-muted-foreground transition-colors hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          <LegalFilters filter={filter} onChange={handleFilter} />

          {loading ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading documents…
            </div>
          ) : visible.length === 0 ? (
            <p data-testid="legal-no-results" className="rounded-lg border border-border bg-card px-4 py-6 text-center text-sm text-muted-foreground">
              No policies match “{query}”.
            </p>
          ) : (
            <Accordion
              type="single"
              collapsible
              value={open}
              onValueChange={setOpen}
              data-testid="legal-accordion"
              className="overflow-hidden rounded-lg border border-border bg-card"
            >
              {visible.map((doc) => (
                <AccordionItem key={doc.slug} value={doc.slug} className="border-b border-border last:border-b-0">
                  <AccordionTrigger
                    data-testid={`legal-accordion-trigger-${doc.slug}`}
                    className="px-4 py-3.5 font-display text-base font-semibold tracking-tight text-foreground hover:no-underline hover:bg-secondary sm:px-5"
                  >
                    {doc.title}
                  </AccordionTrigger>
                  <AccordionContent
                    data-testid={`legal-accordion-content-${doc.slug}`}
                    className="px-4 sm:px-5"
                  >
                    {open === doc.slug && (
                      <DocContent slug={doc.slug} cached={bodies[doc.slug]} onLoaded={cacheBody} />
                    )}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </div>
      </section>
    </div>
  );
};

export default Legal;
